import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { BottomTabScreenProps } from '@react-navigation/bottom-tabs';
import { useFocusEffect } from '@react-navigation/native';
import type {
  NativeStackNavigationProp,
  NativeStackScreenProps,
} from '@react-navigation/native-stack';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnderlineTabs } from '../../components/UnderlineTabs';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { CompensatoryWorkPanel } from './CompensatoryWorkPanel';
import { getLeaveModuleCapabilities } from '../../config/leaveCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import {
  routes,
  type MainTabParamList,
  type RootStackParamList,
} from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useWorkflowInbox } from '../../hooks/useWorkflowInbox';
import type { AdminLeaveStatusTab } from '../../types/leave';
import type { WorkflowInboxItem } from '../../types/workflow';
import { leaveApprovalListTitle } from '../../utils/approvalRoles';
import { formatAppliedOn } from '../../utils/dateTime';
import { isRejectedWorkflowStatus } from '../../utils/workflowStatus';

type Props =
  | NativeStackScreenProps<RootStackParamList, 'AdminLeaveRequests'>
  | BottomTabScreenProps<MainTabParamList, 'Leave'>;

const STATUS_TABS: readonly AdminLeaveStatusTab[] = [
  'Pending',
  'Approved',
  'Rejected',
  'All',
];

const REQUEST_TABS = ['Leave Requests', 'Compensatory Requests'] as const;
type RequestTab = (typeof REQUEST_TABS)[number];

function matchesStatusTab(
  item: WorkflowInboxItem,
  tab: AdminLeaveStatusTab,
): boolean {
  if (tab === 'All') return true;
  const code = (item.workflowStatusCode || '').toUpperCase();
  const label = item.workflowStatusLabel.toLowerCase();
  if (tab === 'Pending') {
    return (
      code === 'PENDING' ||
      code === 'IN_PROGRESS' ||
      label.includes('pending') ||
      label.includes('progress')
    );
  }
  if (tab === 'Approved') {
    return (
      code === 'APPROVED' || code === 'COMPLETED' || label.includes('approved')
    );
  }
  return code === 'REJECTED' || label.includes('rejected');
}

function leaveTypeLabel(item: WorkflowInboxItem): string {
  if (item.requestSummary?.trim()) return item.requestSummary.trim();
  return item.requestTypeLabel || 'Leave request';
}

function dateRangeLabel(item: WorkflowInboxItem): string {
  const appliedOn = formatAppliedOn(item.submittedAt);
  if (appliedOn !== 'Not available') return `Applied On: ${appliedOn}`;
  return 'Dates unavailable';
}

function LeaveRequestRow({
  item,
  onPress,
  palette,
}: {
  item: WorkflowInboxItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const employeeName =
    item.employee.displayName ||
    item.employee.employeeNumber ||
    'Employee unavailable';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${employeeName}, ${leaveTypeLabel(item)}, ${
        item.workflowStatusLabel
      }`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowMain}>
        <Text style={[styles.employeeName, { color: palette.textPrimary }]}>
          {employeeName}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {leaveTypeLabel(item)}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {dateRangeLabel(item)}
        </Text>
        {isRejectedWorkflowStatus(item.workflowStatusCode) &&
        item.rejectReason?.trim() ? (
          <Text
            style={[styles.meta, { color: palette.textSecondary }]}
            numberOfLines={1}
            ellipsizeMode="tail"
            {...(Platform.OS === 'web'
              ? { title: item.rejectReason.trim() }
              : null)}
          >
            Reject Reason: {item.rejectReason.trim()}
          </Text>
        ) : null}
      </View>
      <LeaveStatusBadge
        label={item.workflowStatusLabel}
        statusCode={item.workflowStatusCode}
      />
    </Pressable>
  );
}

function StatusTabs({
  value,
  counts,
  onChange,
  palette,
}: {
  value: AdminLeaveStatusTab;
  counts: Record<AdminLeaveStatusTab, number>;
  onChange: (tab: AdminLeaveStatusTab) => void;
  palette: ColorPalette;
}) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.tabsContent}
      style={styles.tabsScroll}
    >
      {STATUS_TABS.map(tab => {
        const selected = tab === value;
        const count = counts[tab];
        const label =
          tab === 'Pending' && count > 0 ? `${tab} (${count})` : tab;
        return (
          <Pressable
            key={tab}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={label}
            onPress={() => onChange(tab)}
            style={[
              styles.tab,
              {
                backgroundColor: selected
                  ? `${palette.info}18`
                  : palette.surface,
                borderColor: selected ? palette.info : palette.border,
              },
            ]}
          >
            <Text
              style={[
                styles.tabLabel,
                { color: selected ? palette.info : palette.textPrimary },
              ]}
            >
              {label}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

export function AdminLeaveRequestListScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getLeaveModuleCapabilities(auth.permissions);
  const showBack = route.name === 'AdminLeaveRequests';
  const root =
    typeof navigation.getParent === 'function'
      ? navigation.getParent<NativeStackNavigationProp<RootStackParamList>>()
      : undefined;
  const [statusTab, setStatusTab] = useState<AdminLeaveStatusTab>('Pending');
  const focusCompensatoryWorkRequestId =
    route.name === 'AdminLeaveRequests'
      ? route.params?.compensatoryWorkRequestId
      : undefined;
  const [requestTab, setRequestTab] = useState<RequestTab>(
    focusCompensatoryWorkRequestId
      ? 'Compensatory Requests'
      : 'Leave Requests',
  );
  const inbox = useWorkflowInbox();
  const {
    applyFilters,
    refreshInbox,
    retry,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = inbox;

  useEffect(() => {
    if (focusCompensatoryWorkRequestId) {
      setRequestTab('Compensatory Requests');
    }
  }, [focusCompensatoryWorkRequestId]);

  useFocusEffect(
    useCallback(() => {
      if (!capabilities.canViewAdminRequests) return;
      applyFilters({
        entityType: 'LEAVE_APPLICATION',
        status:
          statusTab === 'All'
            ? 'ALL'
            : (statusTab.toUpperCase() as 'PENDING' | 'APPROVED' | 'REJECTED'),
      });
    }, [applyFilters, capabilities.canViewAdminRequests, statusTab]),
  );

  const leaveItems = useMemo(
    () =>
      items.filter(item => {
        const code = (item.requestTypeCode || '').toUpperCase();
        return (
          code === 'LEAVE_APPLICATION' ||
          code === 'LEAVE_REQUEST' ||
          code === 'LEAVE'
        );
      }),
    [items],
  );

  const counts = useMemo(() => {
    const next: Record<AdminLeaveStatusTab, number> = {
      Pending: 0,
      Approved: 0,
      Rejected: 0,
      All: leaveItems.length,
    };
    for (const item of leaveItems) {
      if (matchesStatusTab(item, 'Pending')) next.Pending += 1;
      if (matchesStatusTab(item, 'Approved')) next.Approved += 1;
      if (matchesStatusTab(item, 'Rejected')) next.Rejected += 1;
    }
    return next;
  }, [leaveItems]);

  const visibleItems = useMemo(
    () => leaveItems.filter(item => matchesStatusTab(item, statusTab)),
    [leaveItems, statusTab],
  );

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more leave requests"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  const emptyMessage =
    statusTab === 'Pending'
      ? 'There are no pending leave requests waiting for approval.'
      : `No ${statusTab.toLowerCase()} leave requests were found.`;

  return (
    <PermissionGuard
      permission={PERMISSIONS.LEAVE_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen} keyboardSafe>
        <ScreenHeader
          title={leaveApprovalListTitle(auth.roles)}
          onBack={showBack ? navigation.goBack : undefined}
        />
        <View style={styles.body}>
          <UnderlineTabs
            options={REQUEST_TABS}
            value={requestTab}
            onChange={setRequestTab}
          />
          <View style={styles.requestContent}>
            {requestTab === 'Compensatory Requests' ? (
              <CompensatoryWorkPanel
                approvalOnly
                focusRequestId={focusCompensatoryWorkRequestId ?? null}
              />
            ) : (
              <>
                <StatusTabs
                  value={statusTab}
                  counts={counts}
                  onChange={setStatusTab}
                  palette={palette}
                />

                {isLoading && leaveItems.length === 0 ? (
                  <View style={styles.center}>
                    <ActivityIndicator
                      color={palette.primary}
                      accessibilityLabel="Loading leave requests"
                    />
                  </View>
                ) : error && leaveItems.length === 0 ? (
                  <ErrorState message={error} onRetry={retry} />
                ) : (
                  <FlatList
                    data={visibleItems}
                    keyExtractor={item => String(item.workflowInstanceId)}
                    refreshControl={
                      <RefreshControl
                        refreshing={isRefreshing}
                        onRefresh={refreshInbox}
                        tintColor={palette.primary}
                      />
                    }
                    onEndReached={loadMore}
                    onEndReachedThreshold={0.4}
                    ListFooterComponent={footer}
                    ListEmptyComponent={
                      <EmptyState
                        title="No leave requests"
                        message={emptyMessage}
                        actionTitle="Retry"
                        onAction={retry}
                      />
                    }
                    contentContainerStyle={styles.listContent}
                    renderItem={({ item }) => (
                      <LeaveRequestRow
                        item={item}
                        palette={palette}
                        onPress={() =>
                          (
                            root ??
                            (navigation as NativeStackNavigationProp<RootStackParamList>)
                          ).navigate(routes.leaveApproval, {
                            workflowInstanceId: item.workflowInstanceId,
                          })
                        }
                      />
                    )}
                  />
                )}
              </>
            )}
          </View>
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.md },
  requestContent: { flex: 1, paddingTop: spacing.md },
  tabsScroll: { flexGrow: 0, marginBottom: spacing.md },
  tabsContent: {
    gap: spacing.sm,
    paddingRight: spacing.md,
  },
  tab: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  tabLabel: {
    ...typography.caption,
    fontWeight: '700',
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowMain: { flex: 1, gap: spacing.xs },
  employeeName: { ...typography.body, fontWeight: '700' },
  meta: { ...typography.caption },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
